import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, ShoppingCart, Star, Tag, Sparkles, Package,
  UtensilsCrossed, Gamepad2, Scissors, Pill, Bone, Shield,
  Bell, Heart, Percent, Gift, Truck, Clock, ChevronRight,
  Dog, Cat, Fish, Bird
} from "lucide-react";

// Categories with benefits
const upcomingCategories = [
  { 
    id: "food", 
    name: "Alimentazione Premium", 
    icon: UtensilsCrossed, 
    color: "from-orange-500 to-amber-500",
    bgColor: "bg-orange-50",
    description: "Crocchette, umido e diete veterinarie",
    benefits: ["Sconti fino al 30%", "Consegna gratuita", "Marchi top"],
    image: "🍖"
  },
  { 
    id: "health", 
    name: "Salute & Integratori", 
    icon: Pill, 
    color: "from-emerald-500 to-teal-500",
    bgColor: "bg-emerald-50",
    description: "Antiparassitari, vitamine, medicinali",
    benefits: ["Prezzi farmacia", "Prodotti veterinari", "Spedizione rapida"],
    image: "💊"
  },
  { 
    id: "insurance", 
    name: "Assicurazioni Pet", 
    icon: Shield, 
    color: "from-blue-500 to-indigo-500",
    bgColor: "bg-blue-50",
    description: "Polizze sanitarie e RC per animali",
    benefits: ["Da €5/mese", "Copertura veterinaria", "RC danni a terzi"],
    image: "🛡️"
  },
  { 
    id: "accessories", 
    name: "Accessori & Cucce", 
    icon: Package, 
    color: "from-purple-500 to-pink-500",
    bgColor: "bg-purple-50",
    description: "Guinzagli, cucce, trasportini, ciotole",
    benefits: ["Design italiano", "Materiali eco", "Personalizzabili"],
    image: "🏠"
  },
  { 
    id: "grooming", 
    name: "Toelettatura", 
    icon: Scissors, 
    color: "from-pink-500 to-rose-500",
    bgColor: "bg-pink-50",
    description: "Shampoo, spazzole, tagliaunghie",
    benefits: ["Bio e naturali", "Per pelo sensibile", "Kit completi"],
    image: "✨"
  },
  { 
    id: "toys", 
    name: "Giochi & Snack", 
    icon: Gamepad2, 
    color: "from-amber-500 to-yellow-500",
    bgColor: "bg-amber-50",
    description: "Giochi interattivi, masticabili, premi",
    benefits: ["Indistruttibili", "Sicuri", "Educativi"],
    image: "🦴"
  },
];

// Special offers preview
const upcomingOffers = [
  {
    title: "Welcome Pack",
    subtitle: "Per i nuovi iscritti",
    discount: "20% OFF",
    description: "Sul primo ordine di cibo premium",
    color: "from-[#FF6B4A] to-[#FF8F6B]",
    icon: Gift
  },
  {
    title: "Abbonamento Cibo",
    subtitle: "Risparmia ogni mese",
    discount: "15% OFF",
    description: "Consegna automatica mensile",
    color: "from-emerald-500 to-teal-500",
    icon: Truck
  },
  {
    title: "Bundle Salute",
    subtitle: "Antiparassitari + Vitamine",
    discount: "25% OFF",
    description: "Pacchetto protezione completa",
    color: "from-blue-500 to-indigo-500",
    icon: Shield
  }
];

export default function Shop() {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail("");
    }
  };

  // Mobile Layout
  if (isMobile) {
    return (
      <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F] pb-28 overflow-x-hidden" data-testid="shop-page-mobile">
        {/* Header */}
        <div className="sticky top-0 z-40 bg-white/90 dark:bg-[#1D1D1F]/90 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
          <div className="px-5 py-4">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center">
                <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
              </button>
              <h1 className="text-xl font-bold text-[#1D1D1F] dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Zampa Shop
              </h1>
            </div>
          </div>
        </div>

        {/* Hero Banner */}
        <div className="px-5 pt-6">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#FF6B4A] to-[#FF8F6B] p-6 text-white">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-2">
                <Clock size={16} />
                <span className="text-sm font-medium text-white/90">Coming Soon</span>
              </div>
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Lo Shop Zampa sta arrivando!
              </h2>
              <p className="text-white/80 text-sm mb-4">
                Cibo premium, accessori, assicurazioni e molto altro a prezzi esclusivi per i membri Zampa.
              </p>
              <div className="flex items-center gap-2">
                <Sparkles size={18} />
                <span className="text-sm font-semibold">Sconti fino al 30% per chi si iscrive ora</span>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Preview */}
        <div className="px-5 pt-8">
          <h3 className="text-lg font-bold text-[#1D1D1F] dark:text-white mb-4">Cosa troverai</h3>
          <div className="space-y-3">
            {upcomingCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div 
                  key={cat.id}
                  className="bg-white dark:bg-white/5 rounded-2xl p-4 border border-black/5 dark:border-white/10"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center flex-shrink-0`}>
                      <Icon className="text-white" size={26} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-bold text-[#1D1D1F] dark:text-white">{cat.name}</h4>
                      <p className="text-sm text-[#86868B] dark:text-gray-400 mb-2">{cat.description}</p>
                      <div className="flex flex-wrap gap-2">
                        {cat.benefits.map((benefit, i) => (
                          <span 
                            key={i}
                            className={`text-xs px-2 py-1 rounded-full ${cat.bgColor} dark:bg-white/10 font-medium`}
                            style={{ color: 'inherit' }}
                          >
                            {benefit}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Special Offers Preview - Grid instead of carousel */}
        <div className="px-5 pt-8">
          <h3 className="text-lg font-bold text-[#1D1D1F] dark:text-white mb-4">Offerte in arrivo</h3>
          <div className="grid grid-cols-2 gap-3">
            {upcomingOffers.slice(0, 4).map((offer, i) => {
              const Icon = offer.icon;
              return (
                <div 
                  key={i}
                  className={`rounded-2xl bg-gradient-to-br ${offer.color} p-4 text-white`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon size={18} />
                    <span className="text-xs font-medium text-white/80">{offer.subtitle}</span>
                  </div>
                  <h4 className="font-bold text-base mb-1">{offer.title}</h4>
                  <div className="text-xl font-bold">{offer.discount}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Notify Me Section */}
        <div className="px-5 pt-8">
          <div className="bg-[#1D1D1F] dark:bg-white/10 rounded-2xl p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <Bell size={20} />
              <h3 className="font-bold">Avvisami al lancio</h3>
            </div>
            {!subscribed ? (
              <form onSubmit={handleSubscribe} className="space-y-3">
                <p className="text-sm text-gray-400">
                  Iscriviti per ricevere offerte esclusive e sconti riservati!
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="La tua email"
                    className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-gray-400 text-sm"
                  />
                  <Button type="submit" className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl px-5">
                    OK
                  </Button>
                </div>
              </form>
            ) : (
              <div className="flex items-center gap-3 py-2">
                <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Sparkles size={20} />
                </div>
                <p className="text-sm">Perfetto! Ti avviseremo al lancio dello shop.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F]" data-testid="shop-page">
      <div className="max-w-7xl mx-auto p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 lg:mb-8">
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-[#1D1D1F] dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Zampa Shop
            </h1>
            <p className="text-sm text-[#86868B] dark:text-gray-400 mt-1">Tutto per il benessere del tuo animale</p>
          </div>
        </div>

        {/* Hero Banner */}
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#FF6B4A] via-[#FF8F6B] to-[#FFB088] p-10 text-white mb-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-1/3 w-64 h-64 bg-white/10 rounded-full translate-y-1/2" />
          
          <div className="relative z-10 max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur">
                <Clock size={18} />
                <span className="font-medium">Coming Soon</span>
              </div>
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur">
                <Sparkles size={18} />
                <span className="font-medium">Sconti esclusivi</span>
              </div>
            </div>
            <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Lo Shop Zampa sta arrivando!
            </h2>
            <p className="text-white/90 text-lg mb-6 max-w-xl">
              Un marketplace dedicato ai pet lover con cibo premium, accessori di design, assicurazioni vantaggiose e molto altro. 
              Prezzi esclusivi per la community Zampa.
            </p>
            
            {/* Notify Form */}
            {!subscribed ? (
              <form onSubmit={handleSubscribe} className="flex gap-3 max-w-md">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Inserisci la tua email"
                  className="flex-1 px-5 py-3 rounded-xl bg-white/20 backdrop-blur border border-white/30 text-white placeholder-white/70"
                />
                <Button type="submit" className="bg-white text-[#FF6B4A] hover:bg-white/90 rounded-xl px-6 font-semibold">
                  Avvisami
                </Button>
              </form>
            ) : (
              <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-white/20 backdrop-blur max-w-md">
                <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
                  <Sparkles className="text-[#FF6B4A]" size={20} />
                </div>
                <p className="font-medium">Iscritto! Ti avviseremo al lancio con offerte esclusive.</p>
              </div>
            )}
          </div>

          {/* Decorative icons */}
          <div className="absolute right-20 top-1/2 -translate-y-1/2 hidden xl:flex flex-col gap-4">
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center rotate-12">
              <Dog className="text-white" size={32} />
            </div>
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center -rotate-6 ml-8">
              <Cat className="text-white" size={32} />
            </div>
            <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center rotate-3">
              <Heart className="text-white" size={32} />
            </div>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="mb-10">
          <h3 className="text-2xl font-bold text-[#1D1D1F] dark:text-white mb-6">Cosa troverai nello shop</h3>
          <div className="grid grid-cols-3 gap-5">
            {upcomingCategories.map((cat) => {
              const Icon = cat.icon;
              return (
                <div 
                  key={cat.id}
                  className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10 hover:shadow-lg transition-shadow cursor-pointer group"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${cat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="text-white" size={32} />
                  </div>
                  <h4 className="font-bold text-lg text-[#1D1D1F] dark:text-white mb-1">{cat.name}</h4>
                  <p className="text-sm text-[#86868B] dark:text-gray-400 mb-4">{cat.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {cat.benefits.map((benefit, i) => (
                      <span 
                        key={i}
                        className={`text-xs px-3 py-1.5 rounded-full ${cat.bgColor} dark:bg-white/10 font-medium text-[#1D1D1F] dark:text-white`}
                      >
                        {benefit}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Special Offers */}
        <div className="mb-10">
          <h3 className="text-2xl font-bold text-[#1D1D1F] dark:text-white mb-6">Offerte esclusive in arrivo</h3>
          <div className="grid grid-cols-3 gap-5">
            {upcomingOffers.map((offer, i) => {
              const Icon = offer.icon;
              return (
                <div 
                  key={i}
                  className={`rounded-2xl bg-gradient-to-br ${offer.color} p-6 text-white`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                      <Icon size={24} />
                    </div>
                    <span className="text-3xl font-bold">{offer.discount}</span>
                  </div>
                  <h4 className="font-bold text-xl mb-1">{offer.title}</h4>
                  <p className="text-sm text-white/80 mb-2">{offer.subtitle}</p>
                  <p className="text-sm text-white/70">{offer.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Back to Dashboard */}
        <div className="mt-8 text-center">
          <Button 
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="rounded-xl px-8 h-12"
          >
            <ArrowLeft size={18} className="mr-2" />
            Torna alla Dashboard
          </Button>
        </div>
      </div>
    </div>
  );
}
